import type { ChildrenProp } from "@charterindex/armoury-www";
import type { JSX, ReactNode } from "react";
import type { ApiService } from "./api/api-service";
import type { ActiveUser, AuthService } from "./auth/auth-service";
import type { PermissionQuery } from "./auth/permission-query";
import type { CurrencyService } from "./currency/currency-service";
import type { ConstantsKey, ReferenceService, SettingsData, SettingsKey } from "./reference/reference-service";
import type { Services } from "./services";
import { createContext, useContext } from "react";
import { useObservable } from "ecce-react";
import { NullContextError } from "@charterindex/armoury-www";
import { TranslationProvider } from "./translation/translation-context";


const servicesContext = createContext<Services | null>(null);

export type ServicesProviderProps = ChildrenProp & {
	services: Services;
};

/**
 * Provides the application's {@link Services} instance.
 *
 * @see {@link useServices()}
 * @see {@link useApi()}
 * @see {@link useAuth()}
 * @see {@link useActiveUser()}
 */
export const ServicesProvider = ({ services, children }: ServicesProviderProps): JSX.Element => (
	<servicesContext.Provider value={ services }>
		<TranslationProvider translation={ services.translation }>
			{ children }
		</TranslationProvider>
	</servicesContext.Provider>
);

/**
 * Get the application's {@link Services} instance.
 */
export const useServices = (): Services => {
	const ctx = useContext(servicesContext);
	if(!ctx) {
		throw new NullContextError("servicesContext", "ServicesProvider");
	}
	return ctx;
};

/**
 * Get the applications {@link ApiService}.
 */
export const useApi = (): ApiService => useServices().api;

/**
 * Get the applications {@link AuthService}.
 */
export const useAuth = (): AuthService => useServices().auth;

/**
 * Query the active User's permissions. Will re-render on masquerade.
 */
export const usePermission = (query: PermissionQuery | undefined | null, fallback = true): boolean => {
	const auth = useAuth();
	useObservable(auth, "activeUser");

	if(!query) {
		return fallback;
	}

	return auth.permissionQuery(query);
};

/**
 * Get the currently logged in User.
 */
export const useActiveUser = (): ActiveUser => {
	const { auth } = useServices();
	useObservable(auth, "activeUser");

	return auth.activeUser;
};

/**
 * Get the application's {@link ReferenceService}.
 */
export const useReference = (): ReferenceService => useServices().reference;

/**
 * Get the values for a particular Constant.
 */
export const useConstants = (constant: ConstantsKey): readonly string[] => (
	useServices().reference.constants(constant)
);

/**
 * Get the value for a particular Setting.
 */
export const useSetting = <K extends SettingsKey>(setting: K): SettingsData[K] => (
	useServices().reference.setting(setting)
);

export const useCurrencyService = (): CurrencyService => useServices().currency;

export const useResolvedValueOrI18nKey = (value: ReactNode | undefined, key: string | undefined, values: Record<string, unknown> | undefined, fallback?: ReactNode): ReactNode => {
	const { translation } = useServices();
	return translation.resolveValueOrI18nKey(value, key, values, fallback);
};
